import { useCallback, useState, useMemo } from 'react';
import {
    ROLE_ADMIN,
    ROLE_VACATION_DP,
    useAuth,
} from '@/contexts/AuthContext';
import { SubTabs } from '@/components/shared/SubTabs';
import { SectionHeader } from '@/components/shared/SectionHeader';
import {
    VacationHelp,
    VACATION_MANAGEMENT_COLLABORATORS_STEPS,
    VACATION_MANAGEMENT_CONFLICTS_STEPS,
    VACATION_MANAGEMENT_HISTORY_STEPS,
    VACATION_MANAGEMENT_PAIDREST_STEPS,
    VACATION_MANAGEMENT_HOLIDAYS_STEPS,
    MANAGEMENT_RULES,
} from '../../components/VacationHelp';
import { VacationDpDecisionModal } from '../../components/VacationDpDecisionModal';
import { VacationCollaboratorHistoryModal } from '../../components/VacationCollaboratorHistoryModal';
import type { DpDecisionSubmit } from '../../components/VacationDpDecisionModal';
import { useVacationDpMutations } from '../../hooks/data';
import { useVacationDpBoard } from '../../hooks/useVacationDpBoard';
import type { VacationDpRow } from '../../models/vacationDpBoard.types';
import { HolidayTab, VacationDpBoard, PaidRestTab, ClientTab, HistoryTab } from './components';
import { TourDemoDpDecision } from './components/tourDemo/TourDemoDpDecision';
import { TourDemoPaidRestHistory } from './components/tourDemo/TourDemoPaidRestHistory';
import {
    PAGE_SUBTITLE,
    PAGE_TITLE,
    TAB_COLLABORATORS,
    TAB_HOLIDAYS,
    TAB_PAID_REST,
    TAB_CLIENT,
    TAB_HISTORY,
} from './vacationManagementPage.definitions';

function TabContent({ children }: { children: React.ReactNode }) {
    return <div className="px-1 py-4 sm:px-2">{children}</div>;
}

export function VacationManagementPage() {
    const { user, userHasRole } = useAuth();

    const canManage =
        userHasRole(ROLE_ADMIN) ||
        userHasRole(ROLE_VACATION_DP);
    const { decideAsDp } = useVacationDpMutations();
    const [selectedRow, setSelectedRow] = useState<VacationDpRow | null>(null);
    const [historyRow, setHistoryRow] = useState<{
        id: string;
        name: string;
        email: string;
    } | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [tourDemo, setTourDemo] = useState<string | null>(null);

    const tourConfig = useMemo(() => {
        const configs = [
            { id: 'management-collaborators' as const, steps: VACATION_MANAGEMENT_COLLABORATORS_STEPS },
            { id: 'management-conflicts' as const, steps: VACATION_MANAGEMENT_CONFLICTS_STEPS },
            { id: 'management-history' as const, steps: VACATION_MANAGEMENT_HISTORY_STEPS },
            { id: 'management-paidrest' as const, steps: VACATION_MANAGEMENT_PAIDREST_STEPS },
            { id: 'management-holidays' as const, steps: VACATION_MANAGEMENT_HOLIDAYS_STEPS },
        ];
        return configs[activeTab] || configs[0];
    }, [activeTab]);

    const {
        rows,
        visibleRows,
        counts,
        totalCount,
        visibleCount,
        activeFilter,
        toggleFilter,
        search,
        setSearch,
        selectedMonth,
        setSelectedMonth,
        selectedYear,
        setSelectedYear,
        availableYears,
        emptyReason,
        isLoading,
        error,
        isAccessDenied,
        selectedClient,
        setSelectedClient,
        clientOptions,
    } = useVacationDpBoard();

    const handleConfirm = useCallback(
        (row: VacationDpRow, decision: DpDecisionSubmit) => {
            if (!row.request || !user?.email) return;

            const segmentDecisionsArray = Object.entries(decision.segmentDecisions).map(([order, item]) => ({
                order: parseInt(order, 10),
                kind: item.decision as 'approve' | 'reject',
                note: item.note,
            }));

            decideAsDp.mutate(
                {
                    requestId: row.request.id,
                    segmentDecisions: segmentDecisionsArray,
                    sellDecision: decision.sellDecision,
                    dpEmail: user.email,
                    collaboratorId: row.collaboratorId,
                    collaboratorEmail: row.collaboratorEmail,
                },
                { onSuccess: () => setSelectedRow(null) }
            );
        },
        [decideAsDp, user?.email]
    );

    return (
        <div className="space-y-8 pb-12">
            <SectionHeader
                title={PAGE_TITLE}
                subtitle={PAGE_SUBTITLE}
                actions={
                    <VacationHelp
                        tourId={tourConfig.id}
                        steps={tourConfig.steps}
                        rules={MANAGEMENT_RULES}
                        autoStartTour={false}
                        onDemoChange={setTourDemo}
                        onTourEnd={() => setTourDemo(null)}
                    />
                }
            />

            <SubTabs
                onChange={setActiveTab}
                variant="secondary"
                tabs={[
                    {
                        name: TAB_COLLABORATORS,
                        count: totalCount,
                        content: (
                            <TabContent>
                                <VacationDpBoard
                                    rows={visibleRows}
                                    counts={counts}
                                    totalCount={totalCount}
                                    visibleCount={visibleCount}
                                    activeFilter={activeFilter}
                                    onToggleFilter={toggleFilter}
                                    search={search}
                                    onSearchChange={setSearch}
                                    selectedMonth={selectedMonth}
                                    onMonthChange={setSelectedMonth}
                                    selectedYear={selectedYear}
                                    onYearChange={setSelectedYear}
                                    availableYears={availableYears}
                                    emptyReason={emptyReason}
                                    onOpenRequest={setSelectedRow}
                                    onOpenHistory={setHistoryRow}
                                    isLoading={isLoading}
                                    isAccessDenied={isAccessDenied}
                                    hasError={Boolean(error)}
                                    selectedClient={selectedClient}
                                    onClientChange={setSelectedClient}
                                    clientOptions={clientOptions}
                                />
                            </TabContent>
                        ),
                    },
                    {
                        name: TAB_CLIENT,
                        content: (
                            <TabContent>
                                <ClientTab onOpenRequest={setSelectedRow} isTourDemo={tourDemo === 'conflicts'} />
                            </TabContent>
                        ),
                    },
                    {
                        name: TAB_HISTORY,
                        content: (
                            <TabContent>
                                <HistoryTab isTourDemo={tourDemo === 'history'} />
                            </TabContent>
                        ),
                    },
                    {
                        name: TAB_PAID_REST,
                        content: (
                            <TabContent>
                                <PaidRestTab />
                            </TabContent>
                        ),
                    },
                    {
                        name: TAB_HOLIDAYS,
                        content: (
                            <TabContent>
                                <HolidayTab canEdit={canManage} />
                            </TabContent>
                        ),
                    },
                ]}
            />

            <VacationDpDecisionModal
                isOpen={Boolean(selectedRow)}
                row={selectedRow}
                onClose={() => setSelectedRow(null)}
                onConfirm={handleConfirm}
                isSubmitting={decideAsDp.isPending}
                allRows={rows}
                isOwnRequest={selectedRow && !userHasRole('admin') ? selectedRow.collaboratorEmail?.toLowerCase() === user?.email?.toLowerCase() : false}
            />

            <VacationCollaboratorHistoryModal
                isOpen={Boolean(historyRow)}
                collaborator={historyRow}
                onClose={() => setHistoryRow(null)}
            />

            {tourDemo === 'dp-decision' && <TourDemoDpDecision />}
            {tourDemo === 'paidrest-history' && <TourDemoPaidRestHistory />}
        </div>
    );
}

export default VacationManagementPage;
